/**
 * Phase 12.6-3A — Platform Users console types.
 *
 * These mirror the shipped backend DTOs in
 * `backend/src/platform-auth/dto/platform-user-admin.dto.ts` field-for-field:
 * `PlatformUserAdminResponseDto`, `CreatePlatformUserDto`,
 * `UpdatePlatformUserIdentityDto`, `UpdatePlatformUserRoleDto` and
 * `UpdatePlatformUserActiveDto`.
 *
 * Design of record: `PLATFORM_USERS_DESIGN.md` v1.0 §4.7, §5.2.
 * Backend contract verified by Phase 12.6-1 and 12.6-2 (141 suites / 3744 tests).
 *
 * ── Deliberately absent ─────────────────────────────────────────────────────
 * `passwordHash`, `tokenVersion`, `passwordChangedAt` and `updatedAt` are NOT
 * declared here, because the API never returns them. The service selects a
 * fixed eight-field allow-list (`PUBLIC_FIELDS`) on every Prisma path, so the
 * password hash is not merely unrendered — it is never fetched from the
 * database at all. Phase 12.6-2 §3.1 proves this across `findMany`, both
 * `findUnique` calls, `create` and all four `update` calls.
 *
 * `tenantId` is absent because `platform_users` has no such column: the
 * platform plane and the tenant plane share no key (D-040). `permissions` is
 * absent because the API never sends them — authority is resolved from the
 * frozen `PLATFORM_PERMISSION_MATRIX` by `role` alone (D-041, D-042).
 *
 * Declaring any of those here would be an invented field: it would type-check,
 * read as contract, and always be `undefined`.
 */

/**
 * The seven platform roles, verbatim from D-041.
 *
 * FROZEN vocabulary — a role cannot be added here without an ADR. The backend
 * rejects anything outside this set at the validation pipe (`@IsIn`) *and*
 * again at the service layer (`isPlatformRole`), so a value not in this union
 * could never be accepted anyway.
 *
 * Kept as a union rather than an enum so it is structurally identical to the
 * `string` the API sends, and so an unexpected value from a future backend is a
 * compile-time conversation rather than a runtime crash.
 */
export type PlatformRole =
  | 'platform-owner'
  | 'platform-admin'
  | 'platform-operations'
  | 'platform-finance'
  | 'platform-support'
  | 'platform-developer'
  | 'platform-auditor';

/** The seven roles as a value, for iteration (filters, selects). Order = D-041. */
export const PLATFORM_ROLES: readonly PlatformRole[] = [
  'platform-owner',
  'platform-admin',
  'platform-operations',
  'platform-finance',
  'platform-support',
  'platform-developer',
  'platform-auditor',
] as const;

/**
 * Mirrors `PlatformUserAdminResponseDto` — the response of all six routes that
 * return a user. Exactly eight fields, and exactly the backend's allow-list.
 *
 * `role` is typed `string`, not `PlatformRole`, on purpose: this describes what
 * the API *sends*, and an inbound value is untrusted. Narrowing happens at the
 * point of use, so a role the frontend does not recognise renders as unknown
 * rather than being asserted into a union it does not belong to.
 *
 * Dates arrive as ISO-8601 strings over JSON, not as `Date` — the backend DTO
 * declares `Date` because that is what Prisma hands the serializer.
 */
export interface PlatformUserSummary {
  id: string;
  email: string;
  /** Alternate identifier. Nullable, and NOT a platform sign-in identifier. */
  username: string | null;
  displayName: string;
  role: string;
  isActive: boolean;
  /** ISO-8601, or `null` when the account has never signed in — a real state (§5.2). */
  lastLoginAt: string | null;
  /** ISO-8601. */
  createdAt: string;
}

/**
 * Mirrors `CreatePlatformUserDto` — `POST /platform/users`.
 *
 * The password is supplied by the operator and transmitted out of band
 * afterwards (OD-B: direct creation, no invitation). It is write-only: it never
 * appears in any response, is never logged, and is never audited.
 *
 * `password` must be at least 12 characters (`SECURITY_RULES.md`). The backend
 * enforces it; a shorter value is a 400 from the validation pipe.
 */
export interface CreatePlatformUserRequest {
  email: string;
  /** Optional. Omit rather than sending an empty string. */
  username?: string;
  displayName: string;
  role: PlatformRole;
  /** Minimum 12 characters. Write-only — never returned. */
  password: string;
}

/**
 * Mirrors `UpdatePlatformUserIdentityDto` — `PATCH /platform/users/:id`.
 *
 * ── Only three fields, and that is load-bearing ─────────────────────────────
 * `role`, `isActive`, `password`, `passwordHash`, `tokenVersion` and
 * `passwordChangedAt` are absent because the backend DTO omits them, and the
 * global pipe runs `forbidNonWhitelisted` — so sending any of them is a **400**,
 * not a silently ignored field. Role and status keep their own routes, which
 * carry the self-mutation guards; a field that does not exist here cannot be
 * smuggled past them.
 *
 * ── `username: null` clears it ──────────────────────────────────────────────
 * `undefined` means "leave unchanged"; explicit `null` means "clear". The
 * backend distinguishes the two, so the union must survive into the request.
 *
 * ── Changing `email` terminates that user's sessions ────────────────────────
 * The email IS the platform sign-in identifier (verified in 12.6-1 across all
 * nine `platformUser` lookups: login resolves by email only). A change bumps
 * `tokenVersion` and revokes every refresh token. Changing only `displayName`
 * or `username` revokes nothing. The UI must warn before an email change —
 * including when editing your own account, which signs you out.
 */
export interface UpdatePlatformUserIdentityRequest {
  displayName?: string;
  email?: string;
  username?: string | null;
}

/** Mirrors `UpdatePlatformUserRoleDto` — `PATCH /platform/users/:id/role`. */
export interface UpdatePlatformUserRoleRequest {
  role: PlatformRole;
}

/** Mirrors `UpdatePlatformUserActiveDto` — `PATCH /platform/users/:id/active`. */
export interface UpdatePlatformUserActiveRequest {
  isActive: boolean;
}

/**
 * `POST /platform/users/:id/revoke-sessions` returns a message, not a user —
 * the only route in this group that does. Typed rather than `void` so a caller
 * cannot mistake it for one that returns the updated row.
 */
export interface RevokeSessionsResult {
  message: string;
}
